import { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d6h084qu8ojn6g516lmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', address: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-poppins bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-solar-blue mb-6">
              Start Your <span className="text-energy-yellow">Solar Journey</span> Today
            </h1>
            <p className="text-xl text-gray-600">
              Get in touch with our solar experts and discover how much you can save with clean energy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-solar-blue mb-6">Request Your Free Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-energy-yellow focus:border-transparent text-sm"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-energy-yellow focus:border-transparent text-sm"
                    placeholder="(352) 509-6933"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-energy-yellow focus:border-transparent text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-energy-yellow focus:border-transparent text-sm"
                    placeholder="123 Main St, Ocala, FL 34471"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-energy-yellow focus:border-transparent text-sm resize-none"
                    placeholder="Tell us about your solar energy needs..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-energy-yellow text-solar-blue px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-400 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Get Free Quote'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-eco-green/10 border border-eco-green text-eco-green px-4 py-3 rounded-lg text-sm">
                    <i className="ri-check-line mr-2"></i>
                    Thank you! We'll contact you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-300 text-red-600 px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    Something went wrong. Please try again or call us directly.
                  </div>
                )}
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-bold text-solar-blue mb-6">Contact Information</h2>
                <p className="text-gray-600 mb-8">
                  Have questions? Our solar experts are here to help. Reach out by phone, email, or visit our office.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-14 h-14 flex items-center justify-center bg-energy-yellow rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-solar-blue"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-solar-blue mb-1">Phone</h3>
                    <a href="tel:352-509-6933" className="text-gray-600 hover:text-solar-blue transition-colors">
                      352-509-6933
                    </a>
                    <p className="text-sm text-gray-500 mt-1">Mon-Fri: 8:00 AM - 5:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 flex items-center justify-center bg-energy-yellow rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-solar-blue"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-solar-blue mb-1">Address</h3>
                    <p className="text-gray-600">
                      3948 SE 15TH ST<br />
                      OCALA, FL 34471
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 flex items-center justify-center bg-energy-yellow rounded-lg mr-4 flex-shrink-0">
                    <i className="ri-time-line text-2xl text-solar-blue"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-solar-blue mb-1">Business Hours</h3>
                    <p className="text-gray-600">
                      Monday - Friday: 8:00 AM - 5:00 PM<br />
                      Saturday - Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-soft-gray p-6 rounded-xl">
                <h3 className="text-xl font-bold text-solar-blue mb-3">Why Choose Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-eco-green text-lg mr-2"></i>
                    Free, no-obligation consultations
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-eco-green text-lg mr-2"></i>
                    Custom solar solutions for your needs
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-eco-green text-lg mr-2"></i>
                    Licensed and certified professionals
                  </li>
                  <li className="flex items-center text-sm text-gray-700">
                    <i className="ri-check-line text-eco-green text-lg mr-2"></i>
                    25-year warranty on all installations
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-soft-gray">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-solar-blue mb-4">Visit Our Office</h2>
            <p className="text-lg text-gray-600">Located in the heart of Ocala, Florida</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden shadow-2xl"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3478.8!2d-82.1401!3d29.1872!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDExJzE0LjAiTiA4MsKwMDgnMjQuNCJX!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="500"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="BRICK CITY SOLAR Location"
            ></iframe>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;